import React from 'react';
import { X, Shield } from 'lucide-react';

const PrivacyPolicy = ({ onClose }) => {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="privacy-policy-modal">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-600/20 rounded-lg">
              <Shield className="w-5 h-5 text-indigo-400" />
            </div>
            <h2 className="text-xl font-bold text-white">Privacy Policy</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
            data-testid="close-privacy"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto prose prose-invert prose-slate max-w-none">
          <p className="text-slate-400 text-sm mb-6">Last updated: January 20, 2026</p>

          <section className="mb-8">
            <h3 className="text-lg font-bold text-white mb-3">1. Introduction</h3>
            <p className="text-slate-300 leading-relaxed">
              Welcome to FlowGen.social (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered social media content generation platform.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-lg font-bold text-white mb-3">2. Information We Collect</h3>
            <p className="text-slate-300 leading-relaxed mb-3">We collect the following types of information:</p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li><strong className="text-white">Account Information:</strong> Email address, display name, and password (encrypted) when you create an account.</li>
              <li><strong className="text-white">Profile Data:</strong> Business/personal profiles you create, including names, descriptions, websites, and logos.</li>
              <li><strong className="text-white">Usage Data:</strong> Information about how you use our service, including content generation history and preferences.</li>
              <li><strong className="text-white">Payment Information:</strong> Processed securely through Stripe. We do not store your full credit card details.</li>
              <li><strong className="text-white">Device Information:</strong> Browser type, IP address, and device identifiers for security and analytics.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="text-lg font-bold text-white mb-3">3. How We Use Your Information</h3>
            <p className="text-slate-300 leading-relaxed mb-3">We use your information to:</p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Provide and improve our AI content generation services</li>
              <li>Process payments and manage subscriptions</li>
              <li>Personalize your experience and remember your preferences</li>
              <li>Send important service updates and communications</li>
              <li>Detect and prevent fraud or abuse</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="text-lg font-bold text-white mb-3">4. AI-Generated Content</h3>
            <p className="text-slate-300 leading-relaxed">
              Our service uses Google Gemini AI to generate content. Your profile information and instructions are sent to AI services to generate personalized content. We do not use your content to train AI models. Generated content is stored temporarily to display results and is not shared with third parties.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-lg font-bold text-white mb-3">5. Data Sharing</h3>
            <p className="text-slate-300 leading-relaxed mb-3">We may share your information with:</p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li><strong className="text-white">Service Providers:</strong> Firebase (authentication), MongoDB (data storage), Stripe (payments), Google AI (content generation)</li>
              <li><strong className="text-white">Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong className="text-white">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mt-3">
              We do not sell your personal information to third parties.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-lg font-bold text-white mb-3">6. Data Security</h3>
            <p className="text-slate-300 leading-relaxed">
              We implement industry-standard security measures including encryption, secure HTTPS connections, and regular security audits. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-lg font-bold text-white mb-3">7. Your Rights</h3>
            <p className="text-slate-300 leading-relaxed mb-3">You have the right to:</p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Access and download your personal data</li>
              <li>Correct inaccurate information</li>
              <li>Delete your account and associated data</li>
              <li>Opt-out of marketing communications</li>
              <li>Request data portability</li>
            </ul>
          </section>

          <section className="mb-8">
            <h3 className="text-lg font-bold text-white mb-3">8. Cookies</h3>
            <p className="text-slate-300 leading-relaxed">
              We use essential cookies for authentication and session management. We may use analytics cookies to understand how you use our service. You can control cookie settings through your browser.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-lg font-bold text-white mb-3">9. Children&apos;s Privacy</h3>
            <p className="text-slate-300 leading-relaxed">
              Our service is not intended for children under 13. We do not knowingly collect information from children under 13. If you believe we have collected such information, please contact us immediately.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-lg font-bold text-white mb-3">10. Changes to This Policy</h3>
            <p className="text-slate-300 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of significant changes by email or through our service. Your continued use after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-white mb-3">11. Contact Us</h3>
            <p className="text-slate-300 leading-relaxed">
              If you have questions about this Privacy Policy or our data practices, please contact us at:{' '}
              <a href="mailto:social@coolgeek.me" className="text-indigo-400 hover:text-indigo-300">
                social@coolgeek.me
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
